import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, onMounted } from 'vue';

import ProviderPromotionOffer from '@/components/ProviderPromotionOffer.vue';
import TitleDetailInfoRow from '@/pages/titleDetailV1/components/titleDetailInfoRow.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import PictureComp from '@/components/picture/PictureComp.vue';
import TrailerModal from '@/components/TrailerModal.vue';

import { TitleDetail } from '@/interfaces/title-details-graphql';
import { getPromoText } from '@/helpers/provider-promotion';
import { getBackdropClips } from '@/helpers/title-helper';
import { ModalHelper } from '@/helpers/modal-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { ClipProvider, MonetizationType } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { useLanguageStore, useRoutingStore } from '@/helpers/composables/useStores';
import { OfferLocation, trackClickOutGraphql } from '@/helpers/offer-helper';
import { isJustWatchTVPlayBtnLanguageSupported } from '@/helpers/justwatchTv-helper';
import { TrailerClip } from '@/interfaces/trailerClip';
import { getTrailerComponent } from '@/helpers/trailer-helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailHeroV1',
  props: {
    title: null,
    originalTitle: null,
    seasonName: { default: null },
    isAVODActive: { type: Boolean },
    activeClipId: { default: null },
    additionalContexts: { default: () => [] }
  },
  emits: ['setActiveClipId'],
  setup(__props: any, { emit }) {

const props = __props as { title: TitleDetail, originalTitle?: string, seasonName: string | null, isAVODActive?: boolean, activeClipId: string | null, additionalContexts: SnowplowContext[] };

const JustWatchTVPlayBtn = () => import('./JustwatchTVPlayBtn.vue');





const { country, language } = useLanguageStore();
const { activeRoute } = useRoutingStore();

const trackingProviders: Record<TrailerClip['provider'], number> = {
	[ClipProvider.Youtube]: 1,
	[ClipProvider.Videobuster]: 2,
	[ClipProvider.Dailymotion]: 3,
	PLEX: 4,
	JUSTWATCH_TV: 5,
};

const isJustWatchTV = computed(
	() =>
		props.title.justwatchTVOffers.length > 0 &&
		props.title.justwatchTVOffers[0].package.technicalName === 'justwatchtv'
);

const titleClips = computed(() => {
	const PlexClip = props.isAVODActive
		? [
				{
					provider: 'PLEX',
					externalId: props.title.plexPlayerOffers[0].id,
					sourceUrl: props.title.plexPlayerOffers[0].standardWebURL,
				},
		  ]
		: null;

	const JustWatchTVClip = isJustWatchTV.value
		? [
				{
					provider: 'JUSTWATCH_TV',
					externalId: props.title.justwatchTVOffers[0].id,
					sourceUrl: props.title.justwatchTVOffers[0].streamUrl,
				},
		  ]
		: null;

	return JustWatchTVClip || PlexClip || getBackdropClips(props.title);
});

const hasApple = computed(() => props.title.offers.some(offer => offer.package.packageId === 350));

const isProviderPromotion = computed(() => {
	const promotion = getPromoText(country.value, props.title.offers || []);
	if (promotion === null) return null;

	if (hasApple.value && promotion.provider === 9) return false;
	return props.title.offers.some(
		offer => offer.monetizationType === MonetizationType.Flatrate && offer.package.packageId === promotion.provider
	);
});

const titleName = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.title : props.title.content.title;
});

const titleFullPath = computed(() => {
	return props.title.__typename === 'Season' ? props.title.show.content.fullPath : props.title.content.fullPath;
});

const releaseYear = computed(() => {
	return props.title.content.originalReleaseYear;
});

const showTitleId = computed(() =>
	props.title.__typename === 'Season' && !!props.title.show ? props.title.show.id : props.title.id
);

const showOriginalTitle = computed(() => !!props.originalTitle && props.originalTitle !== props.title.content.title);

const isMounted = ref(false);
const hasModalOpened = ref(false);
// we need an additional state that isn't tied to re-renderings, but prevents users from opening multiple modal windows.
const isModalOpening = ref(false);

function handleBackdropClick() {
	openTrailerModal(props.title);
	if ((isJustWatchTV.value || props.isAVODActive) && !isProviderPromotion.value) {
		const titleObjectId = props.title.__typename === 'Season' ? props.title.show.objectId : props.title.objectId;
		const titleObjectType =
			props.title.__typename === 'Season' ? props.title.show.objectType : props.title.objectType;
		trackClickOutGraphql({
			offer: isJustWatchTV.value ? props.title.justwatchTVOffers[0] : props.title.plexPlayerOffers[0],
			offerLocation: OfferLocation.BACKDROP,
			objectId: titleObjectId,
			objectType: titleObjectType,
		});
	}
}

async function openTrailerModal(title: TitleDetail) {
	if (titleClips.value.length === 0 || isProviderPromotion.value) return;
	if (hasModalOpened.value || isModalOpening.value) return;
	isModalOpening.value = true;

	try {
		// INP case: artificially give the JS thread time to breathe by adding a re-render.
		await new Promise(resolve => setTimeout(resolve, 0));
		hasModalOpened.value = true; // important to have this after the setTimeout, so the re-rendering is happening afterwards

		TrackingHelper.trackEvent('youtube_started', {
			action: props.title.objectType.toLowerCase(),
			label: 'title-detail',
			value: trackingProviders[titleClips.value[0].provider as ClipProvider],
		});

		// TITLE_DETAIL_REDESIGN_NEW_BUYBOX
		// trackTitleDetailRedesignNewBuyBoxExp({ action: 'click', label: 'youtube_playing' });
		// TITLE_DETAIL_REDESIGN_NEW_BUYBOX

		emit('setActiveClipId', titleClips.value[0].externalId);
		// wait for modal to be closed again
		await ModalHelper.openModal(
			TrailerModal,
			{
				clips: titleClips,
				titleName: titleName.value,
				source: 'title-detail',
				additionalContexts: [...props.additionalContexts],
			},
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[],
			() => (hasModalOpened.value = false)
		);
	} catch (e) {
		console.error(e);
	} finally {
		isModalOpening.value = false;
		hasModalOpened.value = false;
	}
}

const imageUrls = computed(() => props.title.content.backdrops.map(backdrop => backdrop.backdropUrl));

function handleClickTitleDetailLink() {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'title_clicked',
			label: showTitleId.value,
			property: 'title-header',
		},
		props.additionalContexts
	);
}

onMounted(() => {
	if (props.activeClipId) {
		emit('setActiveClipId', null);
	}
	isMounted.value = true;
	TrackingHelper.trackEvent('clip_spinner', {
		action: 'finished',
		value: 1,
		property: titleClips.value.length > 0 ? titleClips.value[0].provider : 'none',
		nonInteraction: true,
	});

	// INP: preload the trailer component's JS already for slow devices
	getTrailerComponent(titleClips.value[0]?.provider as ClipProvider);
});

watch(activeRoute, () => {
	emit('setActiveClipId', null);
});

return { __sfc: true,JustWatchTVPlayBtn, props, emit, country, language, activeRoute, trackingProviders, isJustWatchTV, titleClips, hasApple, isProviderPromotion, titleName, titleFullPath, releaseYear, showTitleId, showOriginalTitle, isMounted, hasModalOpened, isModalOpening, handleBackdropClick, openTrailerModal, imageUrls, handleClickTitleDetailLink, ProviderPromotionOffer, TitleDetailInfoRow, TitleDetailLink, PictureComp, isJustWatchTVPlayBtnLanguageSupported }
}

})